/**
 * Retained — keep a part of the session alive while it is out of sight.
 *
 * WHY THIS EXISTS
 * Back has to return the person to a screen exactly as they left it: what they
 * typed, what the guide wrote, which screen of the phase they were on. The
 * session used to throw a phase away the moment it was left, so going back
 * rebuilt it from its first screen and asked the model for everything again.
 * Keeping the part MOUNTED (just not visible) means there is nothing to rebuild
 * and nothing to ask again. See lib/sessionBack.ts for how Back uses it.
 *
 * HOW IT HIDES
 * The children are drawn into a holder element that this component moves in and
 * out of the page. While it is the one being shown, the holder sits in the
 * layout; while it is not, the holder is simply taken out of the document.
 * React still owns the children (state, effects and timers all carry on), but
 * nobody can see them, tab to them, hear them, or find them in a screenshot.
 *
 * A first version hid the children with `display: none` instead. That works in
 * a browser, but the hidden screens stay in the document, and every existing
 * test that asks "which step is showing?" through the document (the browser-back
 * suite does) then finds two. Taking the holder out of the document is the same
 * thing to a person and an honest answer to a test.
 *
 * `display: contents` on both wrappers means they add no box of their own, so
 * the session's flex layout is exactly what it was.
 *
 * The holder must be in the document BEFORE the children's first commit work
 * runs, or `autoFocus` on a textarea (which the session screens use so the
 * keyboard opens) would try to focus an element that is not in the page yet and
 * silently do nothing. That is why the slot uses a ref callback and not an
 * effect: ref callbacks run while the tree is being committed, ahead of the
 * children after it in the tree.
 */
import { useCallback, useContext, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ActiveContext } from '../../lib/sessionBack'

interface RetainedProps {
  /** True while this part is the one the person is looking at. */
  active: boolean
  children: ReactNode
}

export default function Retained({ active, children }: RetainedProps) {
  const parentActive = useContext(ActiveContext)

  // One holder for the life of this component. It is only ever moved, never
  // replaced: replacing it would make React treat the children as new.
  const [holder] = useState(() => {
    const el = document.createElement('div')
    el.style.display = 'contents'
    return el
  })

  const slot = useRef<HTMLDivElement | null>(null)
  const activeRef = useRef(active)

  // Where the holder lives while it is shown. Runs as the tree is committed.
  const placeSlot = useCallback((el: HTMLDivElement | null) => {
    slot.current = el
    if (el && activeRef.current) el.appendChild(holder)
  }, [holder])

  // Afterwards, follow `active` as it changes.
  useLayoutEffect(() => {
    activeRef.current = active
    const el = slot.current
    if (!el) return
    if (active) {
      if (holder.parentNode !== el) el.appendChild(holder)
    } else if (holder.parentNode) {
      holder.parentNode.removeChild(holder)
    }
  }, [active, holder])

  return (
    <>
      <div ref={placeSlot} style={{ display: 'contents' }} />
      {createPortal(
        // Inside, "active" means shown AND everything around it shown: a card
        // kept inside a hidden stage is not on screen either.
        <ActiveContext.Provider value={parentActive && active}>{children}</ActiveContext.Provider>,
        holder,
      )}
    </>
  )
}
